/**
 * React hooks for the beta feedback system.
 *
 * Normal auth mode runs these hooks against Convex. Local/demo/rollback modes
 * can render App without ConvexProvider, so this module swaps in local defaults
 * and no-op mutations at module load. Auth/local mode changes already require a
 * page reload elsewhere in the app, which keeps the selected hook set stable.
 */

import { useQuery, useMutation, usePaginatedQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import {
  SELECTABLE_CAMPAIGN_FEATURE_DEFINITIONS,
  CAMPAIGN_FEATURE_FLAG_KEYS,
} from '../../../campaignFeatureAccess.js';
import { shouldForceLocalMode } from '../../../storage/StorageDriver.js';
import { FEATURE_FLAGS } from '../../../storage/featureFlags.js';

const EMPTY_PAGINATED_RESULT = Object.freeze({
  results: [],
  status: 'Exhausted',
  loadMore: () => {},
});

const EMPTY_ARRAY = Object.freeze([]);
const EMPTY_OBJECT = Object.freeze({});

const LOCAL_FEATURE_FLAGS = Object.freeze(
  SELECTABLE_CAMPAIGN_FEATURE_DEFINITIONS.reduce((acc, feature) => {
    const flagKey = CAMPAIGN_FEATURE_FLAG_KEYS[feature.key] || `feature.${feature.key}`;
    acc[flagKey] = {
      enabled: true,
      defaultEnabled: true,
      label: feature.label,
      description: feature.desc,
      updatedAt: null,
      disabledBy: null,
    };
    return acc;
  }, {}),
);

const noopMutation = async () => null;
const noopUploadUrl = async () => null;

const shouldUseFeedbackLocalFallback = () => {
  if (shouldForceLocalMode()) return true;
  if (!FEATURE_FLAGS.useConvexAuth) return true;
  return !process.env.REACT_APP_CONVEX_URL;
};

const localFallbackEnabled = shouldUseFeedbackLocalFallback();

const chooseFeedbackHook = (localHook, convexHook) => (
  localFallbackEnabled ? localHook : convexHook
);

export const useMyPreferences = chooseFeedbackHook(
  function useMyPreferences() {
    return null;
  },
  function useMyPreferences() {
    return useQuery(api.userPreferences.myPreferences, {});
  },
);

export function useIsDev() {
  const prefs = useMyPreferences();
  if (prefs === undefined) return undefined;
  return !!prefs?.isDev;
}

export const useSetNickname = chooseFeedbackHook(
  function useSetNickname() {
    return noopMutation;
  },
  function useSetNickname() {
    return useMutation(api.userPreferences.setNickname);
  },
);

export const useUnreadBugReportCount = chooseFeedbackHook(
  function useUnreadBugReportCount() {
    return 0;
  },
  function useUnreadBugReportCount() {
    return useQuery(api.bugReports.myUnreadCount, {});
  },
);

export const useMyBugReports = chooseFeedbackHook(
  function useMyBugReports() {
    return EMPTY_PAGINATED_RESULT;
  },
  function useMyBugReports(pageSize = 25) {
    return usePaginatedQuery(api.bugReports.list, { mine: true }, { initialNumItems: pageSize });
  },
);

export const useAllBugReports = chooseFeedbackHook(
  function useAllBugReports() {
    return EMPTY_PAGINATED_RESULT;
  },
  function useAllBugReports(pageSize = 25) {
    return usePaginatedQuery(api.bugReports.list, {}, { initialNumItems: pageSize });
  },
);

export const useBugReportsByStatus = chooseFeedbackHook(
  function useBugReportsByStatus() {
    return EMPTY_PAGINATED_RESULT;
  },
  function useBugReportsByStatus(status, pageSize = 25) {
    return usePaginatedQuery(
      api.bugReports.list,
      { status },
      { initialNumItems: pageSize },
    );
  },
);

export const useBugReport = chooseFeedbackHook(
  function useBugReport() {
    return null;
  },
  function useBugReport(reportId) {
    return useQuery(
      api.bugReports.getById,
      reportId ? { reportId } : 'skip',
    );
  },
);

export const useBugReportLeaderboard = chooseFeedbackHook(
  function useBugReportLeaderboard() {
    return EMPTY_ARRAY;
  },
  function useBugReportLeaderboard(limit = 25) {
    return useQuery(api.bugReports.leaderboard, { limit });
  },
);

export const useLastBulkExportAt = chooseFeedbackHook(
  function useLastBulkExportAt() {
    return null;
  },
  function useLastBulkExportAt() {
    return useQuery(api.settings.getLastBulkExportAt, {});
  },
);

export const useFindSimilarBugReports = chooseFeedbackHook(
  function useFindSimilarBugReports() {
    return EMPTY_ARRAY;
  },
  function useFindSimilarBugReports(reportId, limit = 5) {
    return useQuery(
      api.bugReports.findSimilar,
      reportId ? { reportId, limit } : 'skip',
    );
  },
);

export const useDevDashboardStats = chooseFeedbackHook(
  function useDevDashboardStats() {
    return null;
  },
  function useDevDashboardStats() {
    return useQuery(api.devStats.dashboardStats, {});
  },
);

export const useBugReporterUsage = chooseFeedbackHook(
  function useBugReporterUsage() {
    return null;
  },
  function useBugReporterUsage() {
    return useQuery(api.devStats.bugReporterUsage, {});
  },
);

export const useSystemsUsageByDm = chooseFeedbackHook(
  function useSystemsUsageByDm() {
    return null;
  },
  function useSystemsUsageByDm() {
    return useQuery(api.devStats.systemsUsageByDm, {});
  },
);

export const useBumpViewCounter = chooseFeedbackHook(
  function useBumpViewCounter() {
    return noopMutation;
  },
  function useBumpViewCounter() {
    return useMutation(api.viewCounters.bump);
  },
);

export const useViewCounters = chooseFeedbackHook(
  function useViewCounters() {
    return null;
  },
  function useViewCounters() {
    return useQuery(api.viewCounters.byView, {});
  },
);

export const useUserActivity = chooseFeedbackHook(
  function useUserActivity() {
    return null;
  },
  function useUserActivity() {
    return useQuery(api.devStats.userActivity, {});
  },
);

export const useUserDirectory = chooseFeedbackHook(
  function useUserDirectory() {
    return EMPTY_OBJECT;
  },
  function useUserDirectory() {
    return useQuery(api.devStats.userDirectory, {});
  },
);

export const useDashboardInsights = chooseFeedbackHook(
  function useDashboardInsights() {
    return null;
  },
  function useDashboardInsights() {
    return useQuery(api.devStats.dashboardInsights, {});
  },
);

export const useFeatureFlags = chooseFeedbackHook(
  function useFeatureFlags() {
    return LOCAL_FEATURE_FLAGS;
  },
  function useFeatureFlags(options = {}) {
    const flags = useQuery(api.featureFlags.flags, options?.forceLocal ? 'skip' : {});
    return options?.forceLocal ? LOCAL_FEATURE_FLAGS : flags;
  },
);

export const useFeatureEnabled = chooseFeedbackHook(
  function useFeatureEnabled() {
    return true;
  },
  function useFeatureEnabled(key) {
    const flags = useQuery(api.featureFlags.flags, {});
    if (flags === undefined) return true;
    const f = flags[key];
    return f ? f.enabled : true;
  },
);

export const useSetFeatureFlag = chooseFeedbackHook(
  function useSetFeatureFlag() {
    return noopMutation;
  },
  function useSetFeatureFlag() {
    return useMutation(api.featureFlags.setFlag);
  },
);

export const useSubmitBugReport = chooseFeedbackHook(
  function useSubmitBugReport() {
    return noopMutation;
  },
  function useSubmitBugReport() {
    return useMutation(api.bugReports.submit);
  },
);

export const useAddBugReportComment = chooseFeedbackHook(
  function useAddBugReportComment() {
    return noopMutation;
  },
  function useAddBugReportComment() {
    return useMutation(api.bugReports.addComment);
  },
);

export const useDevMarkAddressed = chooseFeedbackHook(
  function useDevMarkAddressed() {
    return noopMutation;
  },
  function useDevMarkAddressed() {
    return useMutation(api.bugReports.devMarkAddressed);
  },
);

export const useDevEditReport = chooseFeedbackHook(
  function useDevEditReport() {
    return noopMutation;
  },
  function useDevEditReport() {
    return useMutation(api.bugReports.devEditReport);
  },
);

export const useDevSetExcludeFromExport = chooseFeedbackHook(
  function useDevSetExcludeFromExport() {
    return noopMutation;
  },
  function useDevSetExcludeFromExport() {
    return useMutation(api.bugReports.devSetExcludeFromExport);
  },
);

export const useReporterMarkResolved = chooseFeedbackHook(
  function useReporterMarkResolved() {
    return noopMutation;
  },
  function useReporterMarkResolved() {
    return useMutation(api.bugReports.reporterMarkResolved);
  },
);

export const useReporterMarkNotFixed = chooseFeedbackHook(
  function useReporterMarkNotFixed() {
    return noopMutation;
  },
  function useReporterMarkNotFixed() {
    return useMutation(api.bugReports.reporterMarkNotFixed);
  },
);

export const useDeleteBugReport = chooseFeedbackHook(
  function useDeleteBugReport() {
    return noopMutation;
  },
  function useDeleteBugReport() {
    return useMutation(api.bugReports.deleteReport);
  },
);

export const useMarkBugReportRead = chooseFeedbackHook(
  function useMarkBugReportRead() {
    return noopMutation;
  },
  function useMarkBugReportRead() {
    return useMutation(api.bugReports.markRead);
  },
);

export const useMarkBulkExported = chooseFeedbackHook(
  function useMarkBulkExported() {
    return noopMutation;
  },
  function useMarkBulkExported() {
    return useMutation(api.bugReports.markBulkExported);
  },
);

export const useAttachScreenshot = chooseFeedbackHook(
  function useAttachScreenshot() {
    return noopMutation;
  },
  function useAttachScreenshot() {
    return useMutation(api.bugReports.attachScreenshot);
  },
);

export const useGenerateScreenshotUploadUrl = chooseFeedbackHook(
  function useGenerateScreenshotUploadUrl() {
    return noopUploadUrl;
  },
  function useGenerateScreenshotUploadUrl() {
    return useMutation(api.bugReports.generateScreenshotUploadUrl);
  },
);
